'use client';
import { useEffect, useState } from 'react';

import ThemeProviderWrapper from '@storefront/app/ThemeProviderWrapper';
import { useIsUserOnline } from '@storefront/hooks/useIsUserOnline';
import { getLogger } from '@storefront/logging';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import { getCurrentProductConfigurationURL } from '@storefront/services/SessionHelperService/sessionHelperService';
import { getCurrentPage } from '@storefront/utils/utils';
import { Button, Modal } from '@veneer/core';
import { usePathname, useRouter } from 'next/navigation';
import { getSession, useSession } from 'next-auth/react';
import { useTranslation } from 'react-i18next';
import { useIdleTimer } from 'react-idle-timer';

import { authFailedRequestsInterceptor } from './request-interceptor';
import { signOutHandler } from '../../../../api/auth/[...nextauth]/auth.client.utils';
import { NamespacesI18n } from '@storefront/i18n';

const logger = getLogger('idle-session-handler');
const FIFTEEN_MINUTES_IN_MS = 15 * 60 * 1000;
const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

const isExpired = (dateString: string) =>
  new Date(dateString).getTime() < Date.now();
export const IdleSessionHandler = () => {
  const router = useRouter();
  const { t } = useTranslation(NamespacesI18n.CHECKOUT);
  const pathname = usePathname();
  const { status, data } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const [shouldShowModal, setShouldShowModal] = useState(
    status !== 'loading' &&
      (requestFailed || status !== 'authenticated' || isExpired(data?.expires))
  );
  const isUserOnline = useIsUserOnline();

  useEffect(() => {
    authFailedRequestsInterceptor(() => setRequestFailed(true));
  }, []);
  const handleClose = () => {
    setIsLoading(true);
    router.push(getCurrentProductConfigurationURL());
    router.refresh();
  };

  const checkSessionExpiration = async () => {
    try {
      const session = await getSession();
      if (session) {
        return isExpired(session.expires);
      }
      return true;
    } catch (error) {
      logger.error({ msg: 'Error getting session', error });
      return true;
    }
  };

  const handleModalInactiveSession = async () => {
    if (isUserOnline) {
      logger.info('Token refresh failed');
      setShouldShowModal(true);
      await DataCollection.sendEvent(uiEvents.sessionExpiredEvent, {
        screenPath: `/MagentoStorefront/${getCurrentPage(pathname)}Page/`,
      });
    }
  };

  const handleModalActiveSession = () => {
    logger.info('Token refresh successful');
    setShouldShowModal(false);
  };

  const onAction = async () => {
    const isSessionExpired = await checkSessionExpiration();
    if (!isSessionExpired) {
      handleModalActiveSession();
      return;
    }
    handleModalInactiveSession();
  };

  useEffect(() => {
    if (status === 'unauthenticated') {
      onAction();
    }
  }, [status]);

  const onIdle = async () => {
    logger.info('logging out user due to inactivity');
    await signOutHandler({ redirect: false });
    await onAction();
  };

  useIdleTimer({
    timeout: FIFTEEN_MINUTES_IN_MS,
    onAction,
    throttle: FIVE_MINUTES_IN_MS, // check session expiration every 5 minutes
    onIdle, // logout user when idle timeout is reached
  });

  useEffect(() => {
    onAction();
  }, []);

  if (!shouldShowModal) return null;
  return (
    <ThemeProviderWrapper>
      <Modal
        closeButton={!isLoading}
        show={shouldShowModal}
        onClose={handleClose}
        title={t('checkout.session.idleModal.title')}
        footer={
          <Button
            onClick={handleClose}
            loading={isLoading}
            disabled={isLoading}
          >
            {t('checkout.session.idleModal.button.label')}
          </Button>
        }
      >
        <p>{t('checkout.session.idleModal.description')}</p>
        <p>{t('checkout.session.idleModal.help')}</p>
      </Modal>
    </ThemeProviderWrapper>
  );
};
